/** Enums */
import { ApplicantStatus } from "../../enums/ApplicantStatus.ENUM";

export const validatePassword = {
    fn: (value) => {
        if (!value) return 'Password is required';
        if (value.length < 8) return 'Password must be at least 8 characters';
        if (!/[A-Z]/.test(value))
            return 'Password must contain at least one uppercase letter';
        if (!/\d/.test(value))
            return 'Password must contain at least one number';
        if (!/[^a-zA-Z0-9]/.test(value))
            return 'Password must contain at least one special character';
        return undefined;
    }
};

export const validateConfirmPassword = {
    fn: (value, allValues) => {
        const passwordValue = allValues?.password;
        if (!value) return 'Please confirm your password';
        if (value !== passwordValue) return 'Passwords do not match';
        return undefined;
    }
};

export const validateEmail = {
    fn: (value) => {
        if (!value) return 'Email is required';
        if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value))
            return 'Invalid email address';
        return undefined;
    }
};

export const validatePhone = {
    fn: (value) => {
        if (!value) return "Phone is required";

        let number = value.replace(/^\+\d{1,2}/, "");

        if (number.length !== 10) {
            return "Enter a valid 10-digit phone number";
        }
        return undefined;
    }
};

export const validateSmtpHost = {
    fn: (value) => {
        if (!value) return 'SMTP Host is required';

        const hostnameRegex =
            /^(localhost|(([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})|(\d{1,3}\.){3}\d{1,3})$/;

        if (!hostnameRegex.test(value))
            return 'Enter a valid SMTP host (e.g. smtp.gmail.com)';

        return undefined;
    }
};

export const validateSmtpPort = {
    fn: (value) => {
        if (!value) return 'SMTP Port is required';

        const port = Number(value);

        if (Number.isNaN(port))
            return 'SMTP Port must be a number';

        if (port < 1 || port > 65535)
            return 'SMTP Port must be between 1 and 65535';

        return undefined;
    }
};


export const validateGmailAppPassword = {
    fn: (value) => {
        if (!value) return 'App password is required';

        // basic sanity check
        if (value.length < 16)
            return 'App Password must be exactly 16 characters long';

        return undefined;
    }
};

// Client ID (UUID)
export const validateClientId = {
    fn: (value) => {
        if (!value) return 'Client ID is required';

        const uuidRegex =
            /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

        if (!uuidRegex.test(value)) return 'Client ID must be a valid UUID';

        return undefined;
    }
};

// Tenant ID (UUID)
export const validateTenantId = {
    fn: (value) => {
        if (!value) return 'Tenant ID is required';

        const uuidRegex =
            /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

        if (!uuidRegex.test(value)) return 'Tenant ID must be a valid UUID';

        return undefined;
    }
};

// Client Secret
export const validateClientSecret = {
    fn: (value) => {
        if (!value) return 'Client Secret is required';

        if (value.length < 16)
            return 'Client Secret must be at least 16 characters long';

        if (/\s/.test(value)) return 'Client Secret must not contain spaces';

        return undefined;
    }
};

// URL validation
export const validateUrl = {
    fn: (value) => {
        if (!value) return undefined;
        try {
            const parsed = new URL(value);
            if (parsed.protocol === 'http:' || parsed.protocol === 'https:') return undefined;
            return 'Please enter a valid URL (starting with http:// or https://)';
        } catch {
            return 'Please enter a valid URL (starting with http:// or https://)';
        }
    }
};

const hasValue = (val) => val !== null && val !== undefined && val !== "";

const validateExperienceRange = (expFrom, expTo) => {
    if (expFrom > 99 || expTo > 99) {
        return { isValid: false, message: "Experience values cannot exceed 99" };
    }

    const hasFrom = hasValue(expFrom);
    const hasTo = hasValue(expTo);

    if (hasFrom && !hasTo) {
        return { isValid: false, message: "Experience To is required if Experience From is provided" };
    }

    let cleanedFrom = expFrom;
    if (hasTo && !hasFrom) {
        cleanedFrom = 0;
    }

    if (hasValue(cleanedFrom) && hasTo && Number(expTo) < Number(cleanedFrom)) {
        return { isValid: false, message: "Experience To must be greater than Experience From" };
    }

    return { isValid: true, cleanedFrom };
};

const validateJobTemplateSkills = (skills) => {
    for (const skill of skills) {
        const skillIdEmpty = Array.isArray(skill?.skillId) ? skill.skillId.length === 0 : !skill?.skillId;
        if (!skill?.skillCategoriesId || skillIdEmpty) {
            return { isValid: false, message: "Please select a Category and Skill for all skill rows." };
        }
    }

    for (const skill of skills) {
        const exp = skill?.minimumExperience;
        if (!hasValue(exp) || exp < 0 || exp > 99) {
            return { isValid: false, message: "Minimum experience for all skills must be between 0 and 99" };
        }
    }

    return { isValid: true };
};

export const validateJobTemplateData = (formData, skills) => {
    const expFrom = formData?.experienceFrom;
    const expTo = formData?.experienceTo;

    const rangeResult = validateExperienceRange(expFrom, expTo);
    if (!rangeResult.isValid) {
        return rangeResult;
    }

    const skillsResult = validateJobTemplateSkills(skills);
    if (!skillsResult.isValid) {
        return skillsResult;
    }

    const cleanedFrom = rangeResult.cleanedFrom;
    return {
        isValid: true,
        cleanedExperience: {
            experienceFrom: hasValue(cleanedFrom) ? Number(cleanedFrom) : cleanedFrom,
            experienceTo: hasValue(expTo) ? Number(expTo) : expTo
        }
    };
};

export const validateSkillAdd = (skills) => {
    if (skills && skills.length > 0) {
        for (let i = 0; i < skills.length; i++) {
            const skill = skills[i];
            const skillIdEmpty = Array.isArray(skill?.skillId) ? skill.skillId.length === 0 : !skill?.skillId;
            if (!skill?.skillCategoriesId || skillIdEmpty) {
                return {
                    isValid: false,
                    message: `Please select a Category and Skill for row ${i + 1} before adding a new row.`
                };
            }
        }
    }
    return { isValid: true };
};

export const sanitizeExperienceRange = (from, to) => {
    let expFrom = from;
    let expTo = to;
    let changed = false;

    if (expFrom > 99) {
        expFrom = 99;
        changed = true;
    }
    if (expTo > 99) {
        expTo = 99;
        changed = true;
    }

    if (hasValue(expTo) && !hasValue(expFrom)) {
        expFrom = 0;
        changed = true;
    }

    return { changed, experienceFrom: expFrom, experienceTo: expTo };
};

export const validateApplicantInteraction = (data) => {
    if (!data) return false;

    const { applicantStatusId, comments } = data;
    if (!applicantStatusId || !comments?.trim()) return false;

    const statusNum = Number(applicantStatusId);

    if (statusNum === ApplicantStatus.FOLLOW_UP) {
        return !!(data.followupReasonId && data.followUpDateTime);
    }
    if (statusNum === ApplicantStatus.REJECTED) {
        return !!data.rejectedReasonId;
    }
    if (statusNum === ApplicantStatus.SCHEDULE || statusNum === ApplicantStatus.READY_FOR_NEXT_ROUND) {
        const isManual = data.scheduleMode === "Manual";
        const hasTime = isManual ? !!data.manualStartTime : !!(data.InterviewerCalendarSlotId || data.ScheduleDatetime);
        return !!(data.interviewRoundId && data.InterviewerId && hasTime);
    }

    return true;
};

export const validateTestTemplateDuration = (value) => {
    if (!value || String(value).trim() === "") return "Duration is required";
    const n = Number(value);
    if (isNaN(n) || n <= 0) return "Duration must be greater than 0";
    if (n > 360) return "Duration cannot exceed 360 minutes";
    return undefined;
};

export const validateTestTemplateTotalMarks = (value) => {
    if (!value || String(value).trim() === "") return "Total Marks is required";
    const n = Number(value);
    if (isNaN(n) || n <= 0) return "Must be greater than 0";
    if (n > 100) return "Cannot exceed 100";
    return undefined;
};

export const validateTestTemplatePassingScore = (value, totalMarks) => {
    if (!value || String(value).trim() === "") return "Passing Score is required";
    const score = Number(value);
    if (isNaN(score) || score <= 0) return "Must be greater than 0";
    if (!totalMarks || String(totalMarks).trim() === "") return undefined;
    const marks = Number(totalMarks);
    if (isNaN(marks)) return undefined;
    if (marks <= 0) return undefined;
    if (score >= marks) {
        return `Must be less than Total Marks (${marks})`;
    }
    return undefined;
};

export const validateTestTemplateGeneralInfo = (data = {}) => {
    const errors = {};

    if (!data.name?.trim()) {
        errors.name = "Test Name is required";
    }

    if (!data.code?.trim()) {
        errors.code = "Test Code is required";
    }

    if (!data.testPurposeId) {
        errors.testPurposeId = "Test Purpose is required";
    }

    if (!data.testModeId) {
        errors.testModeId = "Test Mode is required";
    }

    const durationErr = validateTestTemplateDuration(data.durationMinutes);
    if (durationErr) errors.durationMinutes = durationErr;

    const totalMarksErr = validateTestTemplateTotalMarks(data.totalMarks);
    if (totalMarksErr) errors.totalMarks = totalMarksErr;

    const passingScoreErr = validateTestTemplatePassingScore(data.passingScore, data.totalMarks);
    if (passingScoreErr) errors.passingScore = passingScoreErr;

    return errors;
};

export const validateMinimumWorkflowStage = {
    fn: (value) => {
        if (value === null || value === undefined || value === "") {
            return "workflow stage is required";
        }
        const num = Number(value);
        if (Number.isNaN(num) || !Number.isInteger(num)) {
            return "Must be a valid integer number";
        }
        if (num < 2) {
            return "Value cannot be less than 2";
        }
        return undefined;
    }
};

export const validateExperienceFrom = {
    fn: (value, allValues) => {
        if (value === null || value === undefined || value === "") return undefined;
        const val = Number(value);
        if (isNaN(val) || val < 0) return "Experience must be a positive number";
        if (val > 99) return "Experience values cannot exceed 99";
        
        const expTo = allValues?.experienceTo;
        if (hasValue(expTo)) {
            const valTo = Number(expTo);
            if (!isNaN(valTo) && valTo < val) {
                return "Experience From must be less than or equal to Experience To";
            }
        }
        return undefined;
    }
};

export const validateExperienceTo = {
    fn: (value, allValues) => {
        if (value === null || value === undefined || value === "") {
            const expFrom = allValues?.experienceFrom;
            if (hasValue(expFrom)) {
                return "Experience To is required if Experience From is provided";
            }
            return undefined;
        }
        
        const val = Number(value);
        if (isNaN(val) || val < 0) return "Experience must be a positive number";
        if (val > 99) return "Experience values cannot exceed 99";

        let expFrom = allValues?.experienceFrom;
        if (!hasValue(expFrom)) {
            expFrom = 0;
        }
        const valFrom = Number(expFrom);
        if (!isNaN(valFrom) && val < valFrom) {
            return "Experience To must be greater than or equal to Experience From";
        }
        return undefined;
    }
};
